import React, {useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useOverflowShadow} from 'helpers/hooks/utils';
import StyledDataList from 'components/organisms/Lists/DataList/DataList.styles';
import utils from 'helpers/utils';
import constants from 'helpers/constants';
import LinearProgress from 'components/atoms/Progress/LinearProgress/LinearProgress';
import List from 'components/atoms/Lists/List/List';
import Box from 'components/atoms/Layout/Box/Box';
import {H6, P} from 'components/atoms/Text/Typography/Typography';
import ListItem from 'components/atoms/Lists/ListItem/ListItem';
import {withMemo} from 'helpers/wrapper';
import dom from 'helpers/dom';

const DataListItem = withMemo((props) => {
  const {
    item,
    render,
    selected,
    debounce,
    isLoading
  } = props;

  return render?.(
    item, {
      debounce,
      isLoading,
      selected
    }
  );
});

const DataList = React.forwardRef((props, ref) => {
  const {
    count,
    data,
    title,
    selected,
    debounce,
    emptyText,
    dataKey,
    isItemEqual,
    renderItem,
    isLoading,
    loaderCount,
    showProgressBar,
    orientation,
    eagerFetch,
    ListProps,
    ListItemProps,
    onSelectionChange,
    onHighlightChange,
    onVisibilityChange,
    onFetchMore,
    ...innerProps
  } = useComponentProps({
    debounce: false,
    orientation: 'vertical',
    loaderCount: 16,
    overflowFooter: true,
    ...props
  }, 'DataList', {
    children: ['list'],
    styled: ['overflowHeader', 'overflowFooter'],
    static: ['empty'],
    variable: ['orientation']
  });

  const innerRef = useRef(null);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const overflowHeaderRef = useRef(null);
  const overflowFooterRef = useRef(null);
  const fetchingMoreRef = useRef({});
  const visibilityRef = useRef([]);
  const debounceRef = useRef(false);
  const [internalState, setInternalState] = useState({
    selectedIndex: null,
    scrollElement: null
  });

  const dataList = useMemo(() => ({
    refs: {
      ref: innerRef,
      listRef
    },
    state: {
      ...internalState,
      ...utils.cleanObject({})
    }
  }), [internalState])

  useImperativeHandle(ref, () => dataList);

  const items = useMemo(() => {
    return (isLoading && loaderCount > 0) ? new Array(loaderCount).fill({ loader: true }) : data;
  }, [data, isLoading, loaderCount]);

  const infiniteScroll = Boolean(onFetchMore);
  const hasSelection = Boolean(onSelectionChange);

  const onSelectionChangeEvent = useEffectEvent(onSelectionChange);
  const debouncedSelection = useMemo(() => {
    return utils.debounce((e, idx) => {
      if (debounceRef.current) {
        onSelectionChangeEvent?.(e, idx);
      }
    }, debounce === true ? constants.debounce.selection : (!debounce ? 0 : debounce));
  }, [debounce, onSelectionChangeEvent]);

  const doSelection = useCallback((e, idx, direct = false) => {
    if (direct || !debounce) {
      debounceRef.current = false;
      onSelectionChangeEvent?.(e, idx);
      setInternalState(utils.updater({selectedIndex: idx}, true));
    } else {
      debounceRef.current = true;
      debouncedSelection(e, idx);
      setInternalState(utils.updater({selectedIndex: idx}, true));
    }
  }, [debouncedSelection, debounce, onSelectionChangeEvent]);

  const isItemEqualEvent = useEffectEvent(isItemEqual);
  useLayoutEffect(() => {
    if (hasSelection && !isLoading) {
      debounceRef.current = false;
      setInternalState(utils.updater({
        selectedIndex: (utils.isDefined(selected) && items?.length > 0) ? items.findIndex((d) => {
          return isItemEqualEvent ? isItemEqualEvent?.(d, selected) : (dataKey ?
            d[dataKey] === selected[dataKey] : d === selected);
        }) : -1
      }, true));
    }
  }, [hasSelection, isLoading, items, selected, dataKey, isItemEqualEvent]);

  const handleSelectionChange = (e, idx, reason) => {
    doSelection(e, idx, reason === 'mouse');
  }

  useOverflowShadow(dataList.state.scrollElement, overflowHeaderRef.current, overflowFooterRef.current, items?.length >= count, orientation);

  const fetching = (isLoading || showProgressBar);
  const onFetchMoreEvent = useEffectEvent(onFetchMore);
  const fetchMoreOnBottomReached = useCallback(() => {
    if (infiniteScroll) {
      const el = dataList.state.scrollElement;
      if (el) {
        const horizontal = orientation === 'horizontal';
        const scrollSize = horizontal ?
          (el === window ? document.body.scrollWidth : el.scrollWidth) :
          (el === window ? document.body.scrollHeight : el.scrollHeight);

        const scrollPos = horizontal ?
          (el === window ? window.scrollX : el.scrollLeft) :
          (el === window ? window.scrollY : el.scrollTop);
        const clientSize = horizontal ?
          (el === window ? window.innerWidth : el.clientWidth) :
          (el === window ? window.innerHeight : el.clientHeight);

        // 0.5 clientSize start scroll < 24 items grow until
        const itemsThatFit = items?.length > 0 ? (clientSize / (scrollSize / items.length)) : 0;
        const fraction = 0.5 + 0.5 * Math.min(1, Math.max(0, (16 - itemsThatFit) / 16));
        const endOfPage = (scrollSize - scrollPos - clientSize) < ((scrollPos === 0 && !eagerFetch) ? 0 : (clientSize * fraction));

        // reset fetching state
        fetchingMoreRef.current.startedFetching = fetchingMoreRef.current.startedFetching || fetching;
        if ((fetchingMoreRef.current.startedFetching && !fetching) ||
            fetchingMoreRef.current.length !== items?.length ||
            fetchingMoreRef.current.count !== count) {
          fetchingMoreRef.current = {
            length: items?.length,
            count: count
          };
        }

        if (!fetching && items?.length > 0 && scrollSize > 0) {
          if (!fetchingMoreRef.current.fetching && items?.length < count) {
            if (endOfPage && scrollSize > 0) {
              fetchingMoreRef.current.fetching = true;
              onFetchMoreEvent?.();

              return true;
            }
          }
        }
      }
    } else {
      return true;
    }
  }, [infiniteScroll, eagerFetch, orientation, dataList.state.scrollElement, fetching, items?.length, count, onFetchMoreEvent]);

  const onVisibilityChangeEvent = useEffectEvent(onVisibilityChange);
  const calculateVisibleItems = useCallback(() => {
    if (onVisibilityChangeEvent) {
      const el = dataList.state.scrollElement;
      if (el) {
        const itemEls = Array.from(innerRef.current.querySelectorAll('* > .List > .ListItem'));

        if (itemEls.length > 0 && items?.length > 0) {
          const elBox = dom.getBbox(el);

          const lastItemIdx = itemEls.reduce((itemIndex, itemEl) => {
            const itemBox = dom.getBbox(itemEl);
            if (Math.floor(itemBox.bottom) <= Math.ceil(elBox.bottom)) {
              itemIndex = Math.max(items.findIndex((item, idx) => {
                return !item.loader && (
                  dataKey ? item[dataKey].toString() === itemEl.dataset['key']?.toString() : +idx === +itemEl.dataset['key']
                )
              }), itemIndex);
            }

            return itemIndex;
          }, -1);

          if (lastItemIdx !== -1) {
            let visibleItems = items.filter((itm1, idx) => {
              return idx <= lastItemIdx && !visibilityRef.current.find((itm2) => {
                return isItemEqualEvent ? isItemEqualEvent?.(itm1, itm2) : (dataKey ?
                  itm1[dataKey] === itm2[dataKey] : itm1 === itm2);
              })
            });

            if (visibleItems.length > 0) {
              visibilityRef.current = visibilityRef.current.concat(visibleItems);
              onVisibilityChangeEvent?.(visibleItems);

              return true;
            }
          }
        }
      }
    } else {
      return true;
    }
  }, [dataList.state.scrollElement, items, dataKey, isItemEqualEvent, onVisibilityChangeEvent]);

  useEffect(() => {
    if (dataList.state.scrollElement) {
      return utils.observeRetry(fetchMoreOnBottomReached, 5);
    }
  }, [dataList.state.scrollElement, fetchMoreOnBottomReached]);

  useEffect(() => {
    if (dataList.state.scrollElement) {
      return utils.observeRetry(calculateVisibleItems, 5);
    }
  }, [dataList.state.scrollElement, calculateVisibleItems]);

  useEffect(() => {
    if (dataList.state.scrollElement) {
      return utils.observeScroll(dataList.state.scrollElement, () => {
        fetchMoreOnBottomReached();
        calculateVisibleItems();
      });
    }
  }, [dataList.state.scrollElement, fetchMoreOnBottomReached, calculateVisibleItems]);

  useEffect(() => {
    if (dataList.state.scrollElement) {
      return utils.observeResize(dataList.state.scrollElement, () => {
        fetchMoreOnBottomReached();
        calculateVisibleItems();
      });
    }
  }, [dataList.state.scrollElement, fetchMoreOnBottomReached, calculateVisibleItems]);

  const wrapperEl = wrapperRef.current;
  useEffect(() => {
    const element = () => {
      const scrollElement = dom.getScrollElement(wrapperRef.current, orientation === 'horizontal', orientation === 'vertical');
      if (scrollElement) {
        setInternalState((current) => {
          if (current.scrollElement !== scrollElement) {
            return {...current, scrollElement};
          } else {
            return current;
          }
        });
      }
    }

    return utils.observeRetry(element, 8);
  }, [wrapperEl, orientation]);

  const empty = count === 0 && !isLoading;

  innerProps.className = utils.flattenClassName(innerProps.className, {
    empty: empty
  });

  return <StyledDataList ref={innerRef} {...innerProps}>
    <Box ref={overflowHeaderRef} className="DataList-header" />
    <Box ref={wrapperRef} className="DataList-list">
      {title ? <Box className="DataList-title">
        {utils.isReactElement(title) ? title :
          <H6>{title}</H6>}
      </Box> : null}
      {(empty && emptyText) ? <Box className="DataList-empty">
        {utils.isReactElement(emptyText) ? emptyText :
          <P>{emptyText}</P>}
      </Box> : null}
      <List ref={listRef}
            isLoading={isLoading}
            loop={items?.length === count}
            selectedIndex={hasSelection ? dataList.state.selectedIndex : null}
            onSelectionChange={(hasSelection && !isLoading) ? handleSelectionChange : null}
            onHighlightChange={onHighlightChange}
            orientation={orientation}
            {...ListProps}>
        {items?.map((item, idx) => {
          return <ListItem key={item[dataKey] ?? idx}
                           data-key={item[dataKey] ?? idx}
                           data-droppable={item.droppable !== false}
                           density="densest"
                           {...ListItemProps}>
            <DataListItem item={item}
                          render={renderItem}
                          selected={hasSelection ? idx === dataList.state.selectedIndex : false}
                          isLoading={isLoading}/>
          </ListItem>
        })}
      </List>
    </Box>
    <Box ref={overflowFooterRef} className="DataList-footer">
      <LinearProgress className={`DataList-progressBar ${!showProgressBar ? 'hide' : ''}`}/>
    </Box>
  </StyledDataList>
});

DataList.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  count: PropTypes.number,
  data: PropTypes.array,
  title: PropTypes.any,
  selected: PropTypes.any,
  debounce: PropTypes.any,
  emptyText: PropTypes.any,
  dataKey: PropTypes.string,
  isItemEqual: PropTypes.func,
  renderItem: PropTypes.func,
  showProgressBar: PropTypes.bool,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
  eagerFetch: PropTypes.bool,
  isLoading: PropTypes.bool,
  loaderCount: PropTypes.number,
  ListProps: PropTypes.object,
  ListItemProps: PropTypes.object,
  onSelectionChange: PropTypes.func,
  onHighlightChange: PropTypes.func,
  onVisibilityChange: PropTypes.func,
  onFetchMore: PropTypes.func,
  overflowHeader: PropTypes.bool,
  overflowFooter: PropTypes.bool
};

export default DataList;
